import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import type {
  IChartApi,
  ISeriesApi,
  Time,
  IPriceLine,
  CandlestickSeriesPartialOptions,
} from 'lightweight-charts';
import type { GEXLevels } from '../../types/marketData';
import type { OHLCBar } from '../../types/tdSequential';
import { useDailyBars } from './useDailyBars';

// Same palette the Niner widget uses, so the two charts read as one system.
const COLORS = {
  background: '#161616', // Chart Well — spec v2, identical to Panel
  grid: '#161616',
  text: '#c7c2b8', // Text Muted
  candleUp: '#f4f2ed', // Candle Up — monochrome bone
  candleDown: '#61615e', // Candle Down — monochrome gray
  call: '#1c76a2', // Up / Call
  put: '#d8483b', // Down / Put
  buyCount: '#1c76a2', // Buy setup counts — Up/Call blue
  sellCount: '#d8483b', // Sell setup counts — Down/Put red
  completed: '#c2a14e', // Accent Gold — candles at 9 and beyond
};

// Explicit pixel height. The chart element cannot use a percentage height —
// its ancestors are auto-height, so `h-full` would resolve to 0 and
// lightweight-charts' autoSize would measure an empty box and draw nothing.
const CHART_HEIGHT = 320;

// Empty bars held open on the right so candles never run underneath the wall
// labels, which lightweight-charts draws inside the pane at the right edge.
const RIGHT_PAD_BARS = 14;

// Sessions on screen. The series holds a full year — the extra history is
// loaded anyway for the 52-week range, and feeding all of it to the chart
// means the TD counts at the left edge are continuations of real sequences
// rather than restarting at the window boundary. Only the view is narrowed.
const VISIBLE_SESSIONS = 120;

// TD's lookback: each close is compared with the close four bars earlier.
const TD_LOOKBACK = 4;

// Sell counts ride above the high, buy counts below the low — stacking both
// on one side crowded the candles.
const ABOVE_DY = -18;
const BELOW_DY = 8;

interface TDMarker {
  time: string;
  value: number;
  text: string;
  /** Pixel offset from the anchor price. Negative is up the screen. */
  dy: number;
  color: string;
}

/**
 * Extended TD Setup counts, one per bar. Signed: positive is a sell setup,
 * negative a buy setup, zero means no active setup.
 *
 * The backend's `setup_count` recycles at 9 — a tenth consecutive bar starts
 * a fresh sequence at 1. That hides the fact that the setup is still intact,
 * so the count is recomputed here and allowed to run: 9, 10, 11 … until the
 * condition genuinely breaks.
 *
 * The condition is Demark's: a sell setup needs close > close[-4], a buy
 * setup close < close[-4]. The moment a bar fails its side's test — including
 * an exact tie, which satisfies neither — the setup is over and the next bar
 * starts again at 1. Nothing else resets it, and there is no upper bound.
 */
function extendedCounts(bars: OHLCBar[]): number[] {
  const counts = new Array<number>(bars.length).fill(0);
  let run = 0;
  let dir = 0; // 1 = sell setup, -1 = buy setup, 0 = none

  for (let i = TD_LOOKBACK; i < bars.length; i++) {
    const close = bars[i].close;
    const prior = bars[i - TD_LOOKBACK].close;
    const side = close > prior ? 1 : close < prior ? -1 : 0;

    if (side === 0) {
      run = 0;
      dir = 0;
      continue;
    }

    if (side === dir) {
      run += 1;
    } else {
      dir = side;
      run = 1;
    }
    counts[i] = side * run;
  }

  return counts;
}

/** A bar is "completed" once its setup has reached 9 or beyond. */
function isCompleted(count: number): boolean {
  return Math.abs(count) >= 9;
}

/**
 * TD Setup counts.
 *
 * Every bar in a live setup is numbered, running past 9 for as long as the
 * setup holds. Completion is carried by the gold candles, so no glyph is
 * layered on top of the number.
 *
 * Colour follows the setup direction: buy counts blue, sell counts red.
 */
function computeMarkers(bars: OHLCBar[], counts: number[]): TDMarker[] {
  const markers: TDMarker[] = [];

  bars.forEach((bar, i) => {
    const count = counts[i];
    if (!count) return;

    const isBuy = count < 0;
    markers.push({
      time: bar.timestamp.split('T')[0],
      value: isBuy ? bar.low : bar.high,
      text: String(Math.abs(count)),
      dy: isBuy ? BELOW_DY : ABOVE_DY,
      color: isBuy ? COLORS.buyCount : COLORS.sellCount,
    });
  });

  return markers;
}

/** Fixed-height placeholder, so the page does not jump between states. */
function ChartBox({ children, tone = 'muted' }: {
  children: React.ReactNode;
  tone?: 'muted' | 'error';
}): React.ReactElement {
  return (
    <div
      className={`rounded border border-dark-border bg-chart-well flex items-center justify-center text-sm ${
        tone === 'error' ? 'text-bear-red' : 'text-gray-400'
      }`}
      style={{ height: CHART_HEIGHT }}
    >
      {children}
    </div>
  );
}

interface Props {
  symbol: string;
  /** Gamma walls from Fuzion Levels. Drawn onto the chart as price lines. */
  levels?: GEXLevels | null;
}

export function OverviewChart({ symbol, levels }: Props): React.ReactElement {
  // Shared with the hero bar's 5-day change — one request between them.
  const { bars, isLoading, isError } = useDailyBars(symbol);

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Price</h2>
        {!!bars.length && (
          <span className="text-[10px] uppercase tracking-wider text-gray-400">
            Daily &middot; with Niners and Gamma Levels
          </span>
        )}
      </div>

      {/* The chart is mounted only when there is something to draw. It used
          to be permanent, with the empty states layered over it as absolute
          overlays — which lost the stacking contest to the chart canvas and
          left a blank rectangle with no explanation. */}
      {isLoading ? (
        <ChartBox>Loading chart&hellip;</ChartBox>
      ) : bars.length ? (
        <Candles bars={bars} levels={levels} />
      ) : isError ? (
        <ChartBox tone="error">Failed to load price data</ChartBox>
      ) : (
        // No bars is coverage, not a fault: a symbol QF has no history for
        // comes back empty. Said plainly rather than dressed up as an error.
        <ChartBox>No daily price history for {symbol}</ChartBox>
      )}
    </div>
  );
}


/**
 * The chart itself. Mounted only with a non-empty `bars`, so every effect
 * below can assume there is data to render.
 */
function Candles({ bars, levels }: { bars: OHLCBar[]; levels?: GEXLevels | null }): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const priceLinesRef = useRef<IPriceLine[]>([]);
  const markersRef = useRef<TDMarker[]>([]);
  const disposedRef = useRef(false);

  const [visibleMarkers, setVisibleMarkers] = useState<Array<TDMarker & { x: number; y: number }>>([]);

  // Counts are absolutely positioned over the canvas rather than drawn into
  // it, which is how the Niner widget does it — lightweight-charts' built-in
  // markers can't be styled or offset per bar the way this needs.
  const updateVisibleMarkers = useCallback(() => {
    if (disposedRef.current) return;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series || !markersRef.current.length) {
      setVisibleMarkers([]);
      return;
    }

    try {
      const timeScale = chart.timeScale();
      const next: Array<TDMarker & { x: number; y: number }> = [];

      markersRef.current.forEach((marker) => {
        const x = timeScale.timeToCoordinate(marker.time as Time);
        const y = series.priceToCoordinate(marker.value);
        if (x === null || y === null) return;
        next.push({ ...marker, x, y: y + marker.dy });
      });

      setVisibleMarkers(next);
    } catch {
      // chart disposed mid-measure
    }
  }, []);

  // Create the chart once. `autoSize` handles the resize observer for us.
  useEffect(() => {
    if (!containerRef.current) return;
    disposedRef.current = false;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: COLORS.background },
        textColor: COLORS.text,
      },
      grid: {
        vertLines: { color: COLORS.grid },
        horzLines: { color: COLORS.grid },
      },
      crosshair: { mode: 1 },
      rightPriceScale: {
        borderColor: COLORS.grid,
        // Tighter than the 0.2/0.1 default so the candles fill the pane. The
        // top keeps a little more room than the bottom because the TD counts
        // sit above the highs and would otherwise clip out of view.
        scaleMargins: { top: 0.1, bottom: 0.08 },
      },
      timeScale: { borderColor: COLORS.grid, timeVisible: false },
      // Pan/zoom off on purpose: the Overview is a scrolling page, and a live
      // chart would swallow the wheel and zoom instead of scrolling past it.
      // Niner is the place to interrogate the chart.
      handleScroll: false,
      handleScale: false,
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: COLORS.candleUp,
      downColor: COLORS.candleDown,
      borderUpColor: COLORS.candleUp,
      borderDownColor: COLORS.candleDown,
      wickUpColor: COLORS.candleUp,
      wickDownColor: COLORS.candleDown,
      // No autoscale override: the scale fits the candles and nothing else.
      // Price lines are not part of autoscale, so a wall outside the visible
      // price range is drawn off the top or bottom of the pane and does not
      // appear. That is the intended trade — forcing a distant wall into
      // range squashed the price action to make room for it.
    } as CandlestickSeriesPartialOptions);

    chartRef.current = chart;
    seriesRef.current = series;

    chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
      if (!disposedRef.current) requestAnimationFrame(updateVisibleMarkers);
    });

    return () => {
      disposedRef.current = true;
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      // The lines belonged to the removed series — drop the handles so the
      // effect below doesn't try to remove them from a dead chart.
      priceLinesRef.current = [];
      markersRef.current = [];
    };
  }, [updateVisibleMarkers]);

  // Candles and walls are applied together, in one effect, in this order.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !bars.length) return;

    const counts = extendedCounts(bars);

    try {
      series.setData(
        bars.map((b, i) => {
          const base = {
            time: b.timestamp.split('T')[0] as Time,
            open: b.open,
            high: b.high,
            low: b.low,
            close: b.close,
          };
          // Per-bar overrides: once a setup reaches 9 every further bar in it
          // stays gold, so an extended run reads as one block of exhaustion.
          return isCompleted(counts[i])
            ? {
                ...base,
                color: COLORS.completed,
                borderColor: COLORS.completed,
                wickColor: COLORS.completed,
              }
            : base;
        })
      );
    } catch (err) {
      // Logged, not swallowed. If a feed slips something past the sanitiser
      // in the parent, this names it instead of leaving a blank chart.
      console.error('Overview chart: setData rejected these bars', err);
      return;
    }

    markersRef.current = computeMarkers(bars, counts);

    // Not fitContent(): that pins the last bar to the right edge, which is
    // exactly the overlap we're trying to avoid. Extending the logical range
    // past the data leaves RIGHT_PAD_BARS of empty space for the wall labels.
    // The `from` edge windows the year down to the sessions we want on screen.
    chartRef.current?.timeScale().setVisibleLogicalRange({
      from: Math.max(0, bars.length - VISIBLE_SESSIONS) - 0.5,
      to: bars.length - 0.5 + RIGHT_PAD_BARS,
    });

    // ── walls ──────────────────────────────────────────────────────────
    // Cleared unconditionally, then redrawn. A ticker change must not leave
    // the previous symbol's walls painted over the new chart.
    priceLinesRef.current.forEach((line) => {
      try {
        series.removePriceLine(line);
      } catch {
        // already gone
      }
    });
    priceLinesRef.current = [];

    // Drawn after setData: a price line needs a price scale to sit on, and on
    // an empty series there isn't one. Each wall is titled in full, minors
    // dashed so the majors stay the dominant read. A null price is an absent
    // wall, not a zero — nothing is drawn for it.
    const walls: Array<{
      price: number | null | undefined;
      color: string;
      title: string;
      minor?: boolean;
    }> = [
      { price: levels?.call_wall_major, color: COLORS.call, title: 'Call Wall Major' },
      { price: levels?.call_wall_minor, color: COLORS.call, title: 'Call Wall Minor', minor: true },
      { price: levels?.put_wall_minor, color: COLORS.put, title: 'Put Wall Minor', minor: true },
      { price: levels?.put_wall_major, color: COLORS.put, title: 'Put Wall Major' },
    ];

    walls.forEach(({ price, color, title, minor }) => {
      if (price == null) return;
      try {
        priceLinesRef.current.push(
          series.createPriceLine({
            price,
            color,
            lineWidth: 1,
            lineStyle: minor ? 2 : 0,
            axisLabelVisible: true,
            title,
          })
        );
      } catch (err) {
        // Never let a wall take the candles down with it.
        console.error(`Overview chart: could not draw ${title}`, err);
      }
    });

    // The walls only enter the autoscale range on the next rescale, and
    // setData above has already had its.
    chartRef.current?.priceScale('right').applyOptions({ autoScale: true });

    // The range change fires the marker subscription, but the price scale has
    // not finished auto-scaling yet, so y-coordinates would be stale.
    const t = setTimeout(updateVisibleMarkers, 50);
    return () => clearTimeout(t);
  }, [bars, levels, updateVisibleMarkers]);

  return (
    <div
      className="relative rounded border border-dark-border overflow-hidden"
      style={{ height: CHART_HEIGHT }}
    >
      <div ref={containerRef} className="w-full" style={{ height: CHART_HEIGHT }} />

      {/* TD count overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
        {visibleMarkers.map((m, i) => (
          <span
            key={`${m.time}-${m.text}-${i}`}
            style={{
              position: 'absolute',
              left: `${m.x}px`,
              top: `${m.y}px`,
              color: m.color,
              fontSize: '11px',
              fontWeight: 700,
              transform: 'translateX(-50%)',
              textShadow: '0 1px 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.8)',
            }}
          >
            {m.text}
          </span>
        ))}
      </div>
    </div>
  );
}
